import { z } from 'zod';

import type { BusinessModel } from '@/lib/analytics/business-model';
import {
  type GoogleConversionCatalog,
  googleMeasurementStatus,
} from '@/lib/paid-media/google/measurement';
import {
  type LinkedInMeasurementRule,
  defaultConversionIds,
} from '@/lib/paid-media/linkedin/measurement';
import { computedMeasurementOutcome } from '@/lib/paid-media/measurement-outcome';

/**
 * What outcome a Brief's success is, and how each connected ad provider will
 * capture it. Stored on `brief_evidence_decisions.measurement`; the shape is
 * judged here (zod), never in SQL. The plan is computed from data Mora has
 * already synced, so building it costs no provider call.
 */

export const BRIEF_OUTCOMES = ['leads', 'purchases', 'traffic', 'awareness', 'engagement'] as const;
export type BriefOutcome = (typeof BRIEF_OUTCOMES)[number];

export const BRIEF_CAPTURES = [
  'website_conversion',
  'lead_form',
  'pixel_event',
  'conversion_action',
  'on_platform',
  'none',
] as const;
export type BriefCapture = (typeof BRIEF_CAPTURES)[number];

export const MEASUREMENT_PROVIDERS = ['linkedin-ads', 'meta-ads', 'google-ads'] as const;
export type MeasurementProvider = (typeof MEASUREMENT_PROVIDERS)[number];

export const BRIEF_OUTCOME_LABELS: Record<BriefOutcome, string> = {
  leads: 'Leads',
  purchases: 'Purchases',
  traffic: 'Traffic',
  awareness: 'Awareness',
  engagement: 'Engagement',
};

export const MEASUREMENT_PROVIDER_LABELS: Record<MeasurementProvider, string> = {
  'linkedin-ads': 'LinkedIn',
  'meta-ads': 'Meta',
  'google-ads': 'Google',
};

export const BriefMeasurementSchema = z.object({
  version: z.literal(1),
  outcome: z.enum(BRIEF_OUTCOMES),
  capture: z.enum(BRIEF_CAPTURES),
  providers: z.array(
    z.object({
      provider: z.enum(MEASUREMENT_PROVIDERS),
      status: z.enum(['ready', 'needs_setup', 'unread']),
      sourceIds: z.array(z.string()),
      sentence: z.string(),
    }),
  ),
  decidedBy: z.enum(['default', 'user']),
  checkedAt: z.string(),
});

export type BriefMeasurement = z.infer<typeof BriefMeasurementSchema>;
export type BriefMeasurementProvider = BriefMeasurement['providers'][number];

/** Null when the stored value is absent or does not match the current shape. */
export function parseBriefMeasurement(value: unknown): BriefMeasurement | null {
  const parsed = BriefMeasurementSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function defaultBriefOutcome(input: {
  businessModel: BusinessModel | null;
  successMeasureText?: string | null;
  goalText?: string | null;
}): BriefOutcome {
  return computedMeasurementOutcome(input);
}

export interface LinkedInMeasurementSource {
  status: 'ready' | 'unread';
  rules: ReadonlyArray<LinkedInMeasurementRule & { name: string | null }>;
}

export interface MetaMeasurementSource {
  status: 'ready' | 'unread';
  pixels: ReadonlyArray<{ id: string; name: string | null }>;
}

/** The synced customer-level conversion goals, or null catalog when none has been read. */
export interface GoogleMeasurementSource {
  catalog: GoogleConversionCatalog | null;
  fetchedAt: string | null;
}

/** A key is present only when that provider is connected to the workspace. */
export interface MeasurementSources {
  linkedin?: LinkedInMeasurementSource;
  meta?: MetaMeasurementSource;
  google?: GoogleMeasurementSource;
}

function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const CONVERSION_OUTCOMES: ReadonlySet<BriefOutcome> = new Set(['leads', 'purchases']);

function linkedInProvider(
  outcome: BriefOutcome,
  source: LinkedInMeasurementSource,
): { provider: BriefMeasurementProvider; capture: BriefCapture } {
  const counted = outcome === 'leads' ? 'leads' : 'purchases';
  if (outcome === 'leads') {
    // Leads on LinkedIn run as a Lead generation campaign whose form counts
    // them, so the plan no longer depends on a conversion rule existing. Rules
    // that are enabled are still attached and counted separately.
    const ruleIds = source.status === 'ready' ? defaultConversionIds(source.rules) : [];
    const ruleNames = ruleIds.map((id) => {
      const rule = source.rules.find((candidate) => candidate.conversionId === id);
      return rule?.name?.trim() || `rule ${id}`;
    });
    return {
      capture: 'lead_form',
      provider: {
        provider: 'linkedin-ads',
        status: 'ready',
        sourceIds: ruleIds,
        sentence:
          ruleIds.length > 0
            ? `Counted by a LinkedIn lead form that Mora creates with the campaign. Conversion ${ruleIds.length === 1 ? 'rule' : 'rules'} ${joinNames(ruleNames)} ${ruleIds.length === 1 ? 'is' : 'are'} attached as well.`
            : 'Counted by a LinkedIn lead form that Mora creates with the campaign.',
      },
    };
  }
  if (source.status === 'unread') {
    return {
      capture: 'website_conversion',
      provider: {
        provider: 'linkedin-ads',
        status: 'unread',
        sourceIds: [],
        sentence: 'LinkedIn conversion rules not checked yet.',
      },
    };
  }
  const ids = defaultConversionIds(source.rules);
  if (ids.length > 0) {
    const names = ids.map((id) => {
      const rule = source.rules.find((candidate) => candidate.conversionId === id);
      return rule?.name?.trim() || `rule ${id}`;
    });
    return {
      capture: 'website_conversion',
      provider: {
        provider: 'linkedin-ads',
        status: 'ready',
        sourceIds: ids,
        sentence: `Counted by LinkedIn conversion ${ids.length === 1 ? 'rule' : 'rules'} ${joinNames(names)}, attached automatically to campaigns built from this brief.`,
      },
    };
  }
  const allDisabled = source.rules.length > 0;
  return {
    capture: 'website_conversion',
    provider: {
      provider: 'linkedin-ads',
      status: 'needs_setup',
      sourceIds: [],
      sentence: allDisabled
        ? `LinkedIn cannot count ${counted}: every conversion rule on the ad account is disabled. Enable one in LinkedIn Campaign Manager.`
        : `LinkedIn cannot count ${counted}: the ad account has no conversion rule. Create one in LinkedIn Campaign Manager.`,
    },
  };
}

function metaProvider(
  outcome: BriefOutcome,
  source: MetaMeasurementSource,
): { provider: BriefMeasurementProvider; capture: BriefCapture } {
  const counted = outcome === 'leads' ? 'leads' : 'purchases';
  if (source.status === 'unread') {
    return {
      capture: 'pixel_event',
      provider: {
        provider: 'meta-ads',
        status: 'unread',
        sourceIds: [],
        sentence: 'Meta Pixels not checked yet. They are read when you build a Meta campaign.',
      },
    };
  }
  if (source.pixels.length > 0) {
    const names = source.pixels.map((pixel) => pixel.name?.trim() || `Pixel ${pixel.id}`);
    return {
      capture: 'pixel_event',
      provider: {
        provider: 'meta-ads',
        status: 'ready',
        sourceIds: source.pixels.map((pixel) => pixel.id),
        sentence: `Counted by the Meta Pixel ${joinNames(names)}.`,
      },
    };
  }
  return {
    capture: 'pixel_event',
    provider: {
      provider: 'meta-ads',
      status: 'needs_setup',
      sourceIds: [],
      sentence: `Meta cannot count ${counted}: the ad account has no Pixel. Create one in Meta Events Manager.`,
    },
  };
}

function googleProvider(
  outcome: BriefOutcome,
  source: GoogleMeasurementSource,
  checkedAt: string,
): { provider: BriefMeasurementProvider; capture: BriefCapture } {
  const status = googleMeasurementStatus({
    goal: outcome === 'leads' ? 'leads' : 'sales',
    catalog: source.catalog,
    fetchedAt: source.fetchedAt,
    now: new Date(checkedAt),
  });
  const capture: BriefCapture = 'conversion_action';
  if (status.status === 'ready') {
    const names = status.counted.flatMap((goal) => goal.actionNames);
    return {
      capture,
      provider: {
        provider: 'google-ads',
        status: 'ready',
        sourceIds: status.counted.map((goal) => goal.category),
        sentence: `Counted by Google conversion ${names.length === 1 ? 'action' : 'actions'} ${joinNames(names)}.`,
      },
    };
  }
  if (status.status === 'no_conversion_action') {
    return {
      capture,
      provider: {
        provider: 'google-ads',
        status: 'needs_setup',
        sourceIds: [],
        sentence: status.message,
      },
    };
  }
  return {
    capture,
    provider: {
      provider: 'google-ads',
      status: 'unread',
      sourceIds: [],
      sentence:
        status.status === 'stale_catalog'
          ? 'Google conversion goals were last read more than a week ago. Sync Google Ads to check them again.'
          : 'Google conversion goals not checked yet',
    },
  };
}

/**
 * For each connected ad provider, how the outcome will be captured and whether
 * that works today. Outcomes the platform reports natively (traffic,
 * awareness, engagement) need no conversion tracking.
 */
export function briefMeasurementPlan(input: {
  outcome: BriefOutcome;
  sources: MeasurementSources;
  checkedAt: string;
  decidedBy?: 'default' | 'user';
}): BriefMeasurement {
  const { outcome, sources } = input;
  const decidedBy = input.decidedBy ?? 'default';
  const base = { version: 1 as const, outcome, decidedBy, checkedAt: input.checkedAt };

  if (!CONVERSION_OUTCOMES.has(outcome)) {
    const connected: MeasurementProvider[] = [];
    if (sources.linkedin) connected.push('linkedin-ads');
    if (sources.meta) connected.push('meta-ads');
    if (sources.google) connected.push('google-ads');
    const providers: BriefMeasurementProvider[] = connected.map((provider) => ({
      provider,
      status: 'ready',
      sourceIds: [],
      sentence: `${MEASUREMENT_PROVIDER_LABELS[provider]} reports this itself; no conversion tracking needed.`,
    }));
    return { ...base, capture: providers.length > 0 ? 'on_platform' : 'none', providers };
  }

  const results: Array<{ provider: BriefMeasurementProvider; capture: BriefCapture }> = [];
  if (sources.linkedin) results.push(linkedInProvider(outcome, sources.linkedin));
  if (sources.meta) results.push(metaProvider(outcome, sources.meta));
  if (sources.google) results.push(googleProvider(outcome, sources.google, input.checkedAt));
  const lead = results.find((entry) => entry.provider.status === 'ready') ?? results[0];
  return {
    ...base,
    capture: lead ? lead.capture : 'none',
    providers: results.map((entry) => entry.provider),
  };
}

export interface ResolvedBriefMeasurement {
  measurement: BriefMeasurement;
  /** True when nothing is saved and this is computed on the fly. Never written on render. */
  isDefault: boolean;
}

/** The saved measurement if there is one, else the computed default. */
export function resolveBriefMeasurement(input: {
  saved: BriefMeasurement | null | undefined;
  businessModel: BusinessModel | null;
  successMeasureText?: string | null;
  goalText?: string | null;
  sources: MeasurementSources;
  checkedAt: string;
  /** True once the sources are loaded; a saved outcome is then re-checked against them. */
  sourcesReady?: boolean;
}): ResolvedBriefMeasurement {
  if (input.saved) {
    if (!input.sourcesReady) return { measurement: input.saved, isDefault: false };
    return {
      measurement: briefMeasurementPlan({
        outcome: input.saved.outcome,
        sources: input.sources,
        checkedAt: input.checkedAt,
        decidedBy: input.saved.decidedBy,
      }),
      isDefault: false,
    };
  }
  const outcome = defaultBriefOutcome({
    businessModel: input.businessModel,
    successMeasureText: input.successMeasureText,
    goalText: input.goalText,
  });
  return {
    measurement: briefMeasurementPlan({
      outcome,
      sources: input.sources,
      checkedAt: input.checkedAt,
      decidedBy: 'default',
    }),
    isDefault: true,
  };
}
